import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSend, FiClock, FiUser, FiCalendar, FiTag, FiBriefcase, FiImage, FiX } from 'react-icons/fi';
import Toast from '../../components/Toast';
import api from '../../services/api';

const EmployeeTaskDetailPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [reassigning, setReassigning] = useState(false);

  useEffect(() => {
    if (taskId) {
      fetchTaskDetails();
      fetchUsers();
    }
  }, [taskId]);

  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      const taskResponse = await api.post(`/employee/tasks/${taskId}`);
      setTask(taskResponse.data);
      
      try {
        const commentsResponse = await api.get(`/tasks/${taskId}/comments`);
        setComments(commentsResponse.data || []);
      } catch (commentErr) {
        console.error('Error fetching comments:', commentErr);
        setComments([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching task details:', err);
      setError('Failed to load task details: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && selectedImages.length === 0) return;
    
    try {
      setSubmitting(true);
      
      const formData = new FormData();
      formData.append('content', newComment);
      
      selectedImages.forEach((image, index) => {
        formData.append('images', image);
      });
      
      const response = await api.post(`/tasks/${taskId}/comments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setNewComment('');
      setSelectedImages([]);
      setComments(prevComments => [response.data, ...prevComments]);
      setSubmitting(false);
      
      setToast({
        message: 'Comment added successfully',
        type: 'success'
      });
    } catch (err) {
      console.error('Error submitting comment:', err);
      setToast({
        message: 'Failed to submit comment: ' + (err.response?.data?.message || err.message),
        type: 'error'
      });
      setSubmitting(false);
    }
  };
  
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const validImages = files.filter(file => 
      ['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)
    );
    setSelectedImages(prev => [...prev, ...validImages]);
  };
  
  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      await api.put(`/employee/tasks/${taskId}/status`, { status: newStatus });
      setTask(prev => ({ ...prev, status: newStatus }));
      setToast({
        message: 'Task status updated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating task status:', error);
      setToast({
        message: 'Failed to update task status',
        type: 'error'
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', { params: { limit: 1000 } });
      setUsers(response.data.users || response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleReassign = async () => {
    if (!selectedUser) return;
    
    try {
      setReassigning(true);
      await api.put(`/employee/tasks/${taskId}/reassign`, { newUserId: selectedUser });
      setShowReassign(false);
      setSelectedUser('');
      setToast({
        message: 'Task reassigned successfully',
        type: 'success'
      });
      // Redirect back to tasks list since user is no longer assigned
      setTimeout(() => navigate('/employee/tasks'), 2000);
    } catch (error) {
      console.error('Error reassigning task:', error);
      setToast({
        message: 'Failed to reassign task',
        type: 'error'
      });
    } finally {
      setReassigning(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      todo: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      testing: 'bg-purple-100 text-purple-800',
      client_review: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800'
    };
    
    const statusLabels = {
      todo: 'To Do',
      in_progress: 'In Progress',
      testing: 'Testing',
      client_review: 'Client Review',
      completed: 'Completed'
    };
    
    return (
      <span className={`px-3 py-1 text-sm rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-3 py-1 text-sm rounded-full ${priorityClasses[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'None'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading task details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchTaskDetails} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-500">Task not found</div>
        <button 
          onClick={() => navigate(-1)} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <div className="mb-6">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <FiArrowLeft className="mr-2" /> Back to Tasks
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-2xl font-semibold text-gray-900">{task.title}</h1>
            <div className="flex items-center space-x-3">
              {getPriorityBadge(task.priority)}
            </div>
          </div>
          
          {/* Reassign Task */}
          <div className="mb-4">
            <button
              onClick={() => setShowReassign(!showReassign)}
              className="px-3 py-1 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600"
            >
              Reassign Task
            </button>
          </div>
          
          {showReassign && (
            <div className="mb-6 p-4 border border-orange-200 rounded-lg bg-orange-50">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Reassign Task to Another Developer</h3>
              <div className="flex items-center gap-3">
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select a developer</option>
                  {users.filter(user => 
                    user.id !== task.Users?.[0]?.id && 
                    (user.role === 'employee' || user.role === 'manager')
                  ).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email}) - {user.role}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleReassign}
                  disabled={!selectedUser || reassigning}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 text-sm"
                >
                  {reassigning ? 'Reassigning...' : 'Reassign'}
                </button>
                <button
                  onClick={() => setShowReassign(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm"
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs text-orange-600 mt-2">
                Note: After reassigning, you will no longer have access to this task.
              </p>
            </div>
          )}
          
          {/* Status Tabs */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Task Status</label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'todo', label: 'To Do', color: 'gray' },
                { value: 'in_progress', label: 'In Progress', color: 'blue' },
                { value: 'testing', label: 'Testing', color: 'purple' },
                { value: 'client_review', label: 'Client Review', color: 'orange' },
                { value: 'completed', label: 'Completed', color: 'green' }
              ].map(status => (
                <button
                  key={status.value}
                  onClick={() => handleStatusChange(status.value)}
                  disabled={updatingStatus}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    task.status === status.value
                      ? `bg-${status.color}-100 text-${status.color}-800 border-${status.color}-300`
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  } ${updatingStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <div className="mb-4">
                {task.description
                  ? <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: task.description }} />
                  : <p className="text-gray-500 text-sm">No description provided.</p>
                }
              </div>

              {task.Project && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiTag className="mr-2" />
                  <span>Project: {task.Project.name}</span>
                </div>
              )}

              {task.Client && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiBriefcase className="mr-2" />
                  <span>Client: {task.Client.name}</span>
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Task Details</h3>
              
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <FiCalendar className="mr-2" />
                <span>Due Date: {formatDate(task.due_date)}</span>
              </div>
              
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <FiClock className="mr-2" />
                <span>Created: {formatDate(task.created_at)}</span>
              </div>
              
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <FiClock className="mr-2" />
                <span>Updated: {formatDate(task.updated_at)}</span>
              </div>
              
              {task.ReassignedBy && (
                <div className="flex items-center text-sm text-orange-600 mb-2">
                  <FiUser className="mr-2" />
                  <span>Reassigned by: {task.ReassignedBy.name} on {formatDate(task.reassigned_at)}</span>
                </div>
              )}
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Assigned To:</h4>
                <div className="flex flex-wrap gap-2">
                  {task.Users && task.Users.length > 0 ? (
                    task.Users.map(user => (
                      <div key={user.id} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center">
                        <FiUser className="mr-1" /> {user.name}
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-500">Unassigned</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b">
          <h2 className="text-lg font-medium text-gray-900">Comments</h2>
        </div>
        
        <div className="p-6">
          {/* Comment List */}
          <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto">
            {comments && comments.length > 0 ? (
              comments.map(comment => (
                <div key={comment.id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-medium">
                        {comment.User?.name?.charAt(0) || 'U'}
                      </div>
                      <span className="ml-2 font-medium">{comment.User?.name || 'Unknown User'}</span>
                    </div>
                    <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                  {comment.images && (
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {(() => {
                        try {
                          return JSON.parse(comment.images).map((imageUrl, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={`${process.env.REACT_APP_SERVER_URL || 'https://taskflowapi.webseller.in'}${imageUrl}`}
                              alt={`Comment image ${imgIndex + 1}`}
                              className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => window.open(`${process.env.REACT_APP_SERVER_URL || 'https://taskflowapi.webseller.in'}${imageUrl}`, '_blank')}
                            />
                          ));
                        } catch (e) {
                          console.error('Error parsing images:', e);
                          return null;
                        }
                      })()
                      }
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No comments yet</p>
            )}
          </div>
          
          {/* Comment Form */}
          <form onSubmit={handleSubmitComment} className="mt-4">
            <div className="mb-4">
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
                Add a comment
              </label>
              <textarea
                id="comment"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Type your comment here..."
              ></textarea>
            </div>
            
            {/* Image Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attach Images
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  id="images"
                  multiple
                  accept=".png,.jpg,.jpeg"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <label
                  htmlFor="images"
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 cursor-pointer flex items-center"
                >
                  <FiImage className="mr-2" /> Select Images
                </label>
                <span className="text-xs text-gray-500">PNG, JPG, JPEG only</span>
              </div>
              
              {/* Selected Images Preview */}
              {selectedImages.length > 0 && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-20 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <FiX size={12} />
                      </button>
                      <div className="text-xs text-gray-500 mt-1 truncate">
                        {image.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                disabled={submitting || (!newComment.trim() && selectedImages.length === 0)}
              >
                <FiSend className="mr-2" /> {submitting ? 'Sending...' : 'Send Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTaskDetailPage;